import * as React from 'react';
import {
    List,
    Datagrid,
    TextField,
    Edit,
    Create,
    SimpleForm,
    TextInput,
    required,
    regex,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
} from 'react-admin';
import { Tooltip } from '@mui/material';
import { useResourceLimits, useResourceLimitMessage } from '../hooks/useResourceLimits';

// Phone number validation for E.164 format
// E.164: + followed by 1-15 digits (country code + national number)
const phoneValidation = regex(
    /^\+[1-9]\d{0,14}$/,
    'Must be in E.164 format: +[country code][number] (max 15 digits total, e.g., +31612345678)'
);

// Custom actions with limit-aware create button
const ContactListActions = () => {
    const { data: limitsData, invalidate } = useResourceLimits('contacts');
    const limitMessage = useResourceLimitMessage('contacts');
    
    // Invalidate limits when the list might have changed
    React.useEffect(() => {
        // Listen for data changes and invalidate limits
        const handleStorageChange = () => {
            invalidate();
        };
        
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [invalidate]);
    
    const createButton = (
        <CreateButton 
            disabled={limitsData && !limitsData.canCreate}
            sx={{
                ...(limitsData && !limitsData.canCreate && {
                    opacity: 0.5,
                    cursor: 'not-allowed'
                })
            }}
        />
    );
    
    return (
        <TopToolbar>
            <RefreshButton onClick={invalidate} />
            {limitsData && !limitsData.canCreate ? (
                <Tooltip title={limitMessage} placement="top">
                    <span>{createButton}</span>
                </Tooltip>
            ) : (
                createButton
            )}
            <ExportButton />
        </TopToolbar>
    );
};

export const ContactList = () => (
    <List 
        bulkActionButtons={false}
        sort={{ field: 'fullname', order: 'ASC' }}
        actions={<ContactListActions />}
    >
        <Datagrid 
            rowClick="edit" 
            bulkActionButtons={false} 
            sx={{ 
                '& [data-field="__selection__"]': { display: 'none' },
                '& .MuiTableCell-paddingCheckbox': { display: 'none' },
                // Making column headers bold
                '& .MuiTableCell-head': {
                    fontWeight: 'bold',
                }
            }}
        >
            <TextField source="fullname" sortable={true} />
            <TextField source="keyword" sortable={true} />
            <TextField source="phone" sortable={true} />
        </Datagrid>
    </List>
);

const ContactTitle = ({ record }) => {
    return <span>Contact: {record ? record.fullname : ''}</span>;
};

export const ContactEdit = () => (
    <Edit title={<ContactTitle />} mutationMode="pessimistic">
        <SimpleForm>
            <TextInput source="fullname" validate={[required()]} fullWidth />
            <TextInput source="keyword" validate={[required()]} fullWidth />
            <TextInput source="phone" validate={[required(), phoneValidation]} fullWidth />
        </SimpleForm>
    </Edit>
);

export const ContactCreate = () => (
    <Create>
        <SimpleForm>
            <TextInput source="fullname" validate={[required()]} fullWidth />
            <TextInput source="keyword" validate={[required()]} fullWidth />
            <TextInput source="phone" validate={[required(), phoneValidation]} fullWidth />
        </SimpleForm>
    </Create>
);